/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Tab, Tabs } from "@material-ui/core";
import { RouteComponentProps } from "@reach/router";
import { sortBy } from "lodash";
import { Column } from "@material-table/core";
import { CreatePropertyFeatureFlag,
  CreatePropertyFeatureFlagVariables,
  FeatureFlagDashboard,
  FeatureFlagDashboard_propertyFeatureFlags as PropertyFeatureFlagType,
  DeletePropertyFeatureFlag,
  DeletePropertyFeatureFlagVariables,
  UpdatePropertyFeatureFlag,
  UpdatePropertyFeatureFlagVariables } from "types/gql-types";
import { EditableTable } from "components/haven-design/editable-table";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { LoadingSpinner } from "components/graphql-views";
import {
  query,
  updateFeatureFlag,
  createPropertyFeatureFlag,
  updatePropertyFeatureFlag,
  deletePropertyFeatureFlag,
} from "./queries";
import { featureFlagLookup, propertyLookup } from "./utils";
import styles from "./styles.module.css";

enum TabsIndex {
  Global = 0,
  Property = 1,
}

const FeatureFlagsPage: React.FC<RouteComponentProps> = () => {
  const mutationArgs = { refetchQueries: ["FeatureFlagDashboard"] };
  const { openSnackbar } = useSnackbar();

  const [tabIndex, setTabIndex] = React.useState<TabsIndex>(0);
  const { data, loading, error } = useQuery<FeatureFlagDashboard>(query);

  const [updateFeature] = useMutation(updateFeatureFlag, mutationArgs);
  const [createPropertyFeature] =
  useMutation<CreatePropertyFeatureFlag, CreatePropertyFeatureFlagVariables>(
    createPropertyFeatureFlag,
    mutationArgs,
  );
  const [updatePropertyFeature] =
  useMutation<UpdatePropertyFeatureFlag, UpdatePropertyFeatureFlagVariables>(
    updatePropertyFeatureFlag,
    mutationArgs,
  );
  const [deletePropertyFeature] =
  useMutation<DeletePropertyFeatureFlag, DeletePropertyFeatureFlagVariables>(
    deletePropertyFeatureFlag,
    mutationArgs,
  );
  if (loading) return <LoadingSpinner />;

  const featureFlags = sortBy(data?.featureFlags?.map((x) => (
    { ...x })) || [], [(featureFlag) => featureFlag.name]);
  const propertyFeatureFlags = data?.propertyFeatureFlags?.map((x) => ({ ...x })) || [];

  const properties = sortBy(data?.properties || [], [(property) => property.name]);
  const propertyColumns: Array<Column<PropertyFeatureFlagType>> = [
    {
      title: "Property",
      field: "propertyId",
      editable: "onAdd",
      lookup: propertyLookup(properties),
      defaultSort: "asc",
    },
    {
      title: "Feature",
      field: "featureId",
      editable: "onAdd",
      lookup: featureFlagLookup(featureFlags),
    },
    { title: "Active", field: "enabled", type: "boolean", initialEditValue: true },
  ];

  if (error) {
    logger.sendError(error);
    return <div>Error loading feature flags</div>;
  }

  return (
    <div className={styles.container}>
      <h3>Feature Flags</h3>

      <Tabs value={tabIndex} onChange={(_, index) => setTabIndex(index)}>
        <Tab label="Global" />
        <Tab label="Property" />
      </Tabs>

      {tabIndex === TabsIndex.Global && (
        <EditableTable
          title="Global"
          data={featureFlags}
          isLoading={loading}
          options={{ pageSize: 10 }}
          columns={[
            { title: "Feature", field: "name", editable: "never", defaultSort: "asc" },
            { title: "Active", field: "globalEnabled", type: "boolean" },
            { title: "Enabled by Default", field: "globalDefaultEnabled", type: "boolean" },
          ]}
          editable={{
            onRowUpdate (row) {
              const variables = {
                id: row.id,
                globalEnabled: row.globalEnabled,
                globalDefaultEnabled: row.globalDefaultEnabled,
              };

              return updateFeature({ variables }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error updating feature flag", status: "error" });
              });
            },
          }}
        />
      )}

      {tabIndex === TabsIndex.Property && (
        <EditableTable
          title="Property"
          data={propertyFeatureFlags}
          isLoading={loading}
          options={{ pageSize: 25, pageSizeOptions: [5, 10, 20, 25] }}
          columns={propertyColumns}
          editable={{
            onRowAdd (row: PropertyFeatureFlagType) {
              const variables = {
                propertyId: row.propertyId,
                featureId: row.featureId,
                enabled: row.enabled,
              };
              return createPropertyFeature({ variables }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error creating property feature flag", status: "error" });
              });
            },
            onRowUpdate (row: PropertyFeatureFlagType) {
              const variables = { id: row.id, enabled: row.enabled };
              return updatePropertyFeature({ variables }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error updating property feature flag", status: "error" });
              });
            },
            onRowDelete (row: PropertyFeatureFlagType) {
              return deletePropertyFeature({ variables: { id: row.id } }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error deleting property feature flag", status: "error" });
              });
            },
          }}
        />
      )}
    </div>
  );
};

export default FeatureFlagsPage;
